// category-hierarchy.service.ts
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { ApiService } from '@core';
import { Category } from '@models';

export interface AccessorySearchResult {
  accessoryCategories: Category[];
  firstAccessoryCategoryId: string | null;
  firstAccessoryCategoryName: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CategoryHierarchyService {
  private apiService = inject(ApiService);

  private hierarchySubject = new BehaviorSubject<Category | null>(null);
  private isLoading = new BehaviorSubject<boolean>(false);
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

  private hierarchyRequest$: Observable<Category> | null = null;

  constructor() {}

  /**
   * Get the full Apple category hierarchy (cached)
   */
  getHierarchy(): Observable<Category> {
    const cached = this.hierarchySubject.value;
    const isExpired = Date.now() - this.lastFetchTime > this.CACHE_DURATION_MS;

    if (cached && !isExpired) {
      return of(cached);
    }

    if (this.hierarchyRequest$) {
      return this.hierarchyRequest$;
    }

    this.isLoading.next(true);
    this.hierarchyRequest$ = this.apiService.get('categories/60001/hierarchy-topdown').pipe(
      map((response: any) => response || {}),
      tap((hierarchy: Category) => {
        this.hierarchySubject.next(hierarchy);
        this.lastFetchTime = Date.now();
        this.isLoading.next(false);
        this.hierarchyRequest$ = null;
      }),
      catchError((error) => {
        this.isLoading.next(false);
        this.hierarchyRequest$ = null;
        return of({} as Category);
      }),
      shareReplay(1),
    );

    return this.hierarchyRequest$;
  }

  /**
   * Find accessory categories for a given parent category ID
   */
  findAccessoryCategories(parentCategoryId: string): Observable<AccessorySearchResult> {
    return this.getHierarchy().pipe(
      map((hierarchy) => {
        if (!hierarchy) {
          return {
            accessoryCategories: [],
            firstAccessoryCategoryId: '60010',
            firstAccessoryCategoryName: 'Accessories',
          };
        }

        const targetCategory = this.findCategoryById(hierarchy, parentCategoryId);

        if (!targetCategory) {
          return {
            accessoryCategories: [],
            firstAccessoryCategoryId: '60010',
            firstAccessoryCategoryName: 'Accessories',
          };
        }

        // Check if the target category itself is an accessory category
        const isTargetAccessory = targetCategory.name && targetCategory.name.toLowerCase().includes('accessories');

        if (isTargetAccessory) {
          return {
            accessoryCategories: [targetCategory],
            firstAccessoryCategoryId: targetCategory.id.toString(),
            firstAccessoryCategoryName: targetCategory.name,
          };
        }

        // If target is not an accessory, search its subcategories
        if (!targetCategory.subCategories || targetCategory.subCategories.length === 0) {
          return {
            accessoryCategories: [],
            firstAccessoryCategoryId: '60010',
            firstAccessoryCategoryName: 'Accessories',
          };
        }

        // Search for accessory categories within the target's subcategories
        const accessoryCategories = this.searchAccessoriesRecursively(targetCategory.subCategories);

        const firstAccessoryCategoryId =
          accessoryCategories.length > 0 ? accessoryCategories[0].id.toString() : '60010'; // Default to general Apple accessories
        const firstAccessoryCategoryName = accessoryCategories.length > 0 ? accessoryCategories[0].name : 'Accessories'; // Default name

        return { accessoryCategories, firstAccessoryCategoryId, firstAccessoryCategoryName };
      }),
    );
  }

  /**
   * Get cached hierarchy synchronously (returns null if not cached)
   */
  getCachedHierarchy(): Category | null {
    return this.hierarchySubject.value;
  }

  /**
   * Check if hierarchy is currently loading
   */
  isLoadingHierarchy(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  /**
   * Force refresh of hierarchy cache
   */
  refreshHierarchy(): Observable<Category> {
    this.hierarchySubject.next(null);
    this.lastFetchTime = 0;
    this.hierarchyRequest$ = null;
    return this.getHierarchy();
  }

  /**
   * Clear hierarchy cache
   */
  clearCache(): void {
    this.hierarchySubject.next(null);
    this.lastFetchTime = 0;
    this.hierarchyRequest$ = null;
  }

  // Private helper methods
  private findCategoryById(category: Category, targetId: string): Category | null {
    if (!category) return null;

    if (category.id && category.id.toString() === targetId) {
      return category;
    }

    if (category.subCategories && category.subCategories.length > 0) {
      for (const subCategory of category.subCategories) {
        const found = this.findCategoryById(subCategory, targetId);
        if (found) return found;
      }
    }

    return null;
  }

  private searchAccessoriesRecursively(categories: Category[]): Category[] {
    const accessoryCategories: Category[] = [];

    for (const category of categories) {
      if (category.name && category.name.toLowerCase().includes('accessories')) {
        accessoryCategories.push(category);
      }

      if (category.subCategories && category.subCategories.length > 0) {
        const subAccessories = this.searchAccessoriesRecursively(category.subCategories);
        accessoryCategories.push(...subAccessories);
      }
    }

    return accessoryCategories;
  }

  /**
   * Get all accessory categories from the entire hierarchy
   */
  getAllAccessoryCategories(): Observable<Category[]> {
    return this.getHierarchy().pipe(
      map((hierarchy) => {
        if (!hierarchy || !hierarchy.subCategories) {
          return [];
        }
        return this.searchAccessoriesRecursively(hierarchy.subCategories);
      }),
    );
  }

  /**
   * Get category path (breadcrumb) for a given category ID
   */
  getCategoryPath(categoryId: string): Observable<Category[]> {
    return this.getHierarchy().pipe(
      map((hierarchy) => {
        if (!hierarchy) return [];
        return this.findCategoryPath(hierarchy, categoryId) || [];
      }),
    );
  }

  private findCategoryPath(category: Category, targetId: string, currentPath: Category[] = []): Category[] | null {
    const newPath = [...currentPath, category];

    if (category.id && category.id.toString() === targetId) {
      return newPath;
    }

    if (category.subCategories && category.subCategories.length > 0) {
      for (const subCategory of category.subCategories) {
        const foundPath = this.findCategoryPath(subCategory, targetId, newPath);
        if (foundPath) return foundPath;
      }
    }

    return null;
  }
}
